import { useEffect, useState } from "react";
import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { toast } from "sonner";
import { ExternalLink } from "lucide-react";

export default function Settings() {
  const { user, logout } = useAuth();
  const [me, setMe] = useState(null);
  const canSeeMargin = ["founder", "partner", "finance"].includes(user?.role);
  const [rate, setRate] = useState("");
  const [savingRate, setSavingRate] = useState(false);
  const [sheetInput, setSheetInput] = useState("");
  const [sheetUrl, setSheetUrl] = useState(null);
  const [savingSheet, setSavingSheet] = useState(false);
  const canSeeBank = ["founder", "partner"].includes(user?.role);
  const [bank, setBank] = useState(null);
  const [savingBank, setSavingBank] = useState(false);
  const [driveFolders, setDriveFolders] = useState(null);
  const [savingDriveFolders, setSavingDriveFolders] = useState(false);
  const [onboardingLinks, setOnboardingLinks] = useState(null);
  const [savingOnboardingLinks, setSavingOnboardingLinks] = useState(false);
  const [pricingEditors, setPricingEditors] = useState(null);
  const [newPricingEditor, setNewPricingEditor] = useState("");
  const [savingPricingEditors, setSavingPricingEditors] = useState(false);
  const [agencyQuoteRecipients, setAgencyQuoteRecipients] = useState(null);
  const [newAgencyQuoteRecipient, setNewAgencyQuoteRecipient] = useState("");
  const [savingAgencyQuoteRecipients, setSavingAgencyQuoteRecipients] = useState(false);
  // NJM Build Opportunities go-live (2026-09-27): founder-only, stricter than everything else
  // on this page (which is founder/partner) — flipping these changes what real freelancers see
  // and starts a live Notion pull.
  const isFounder = user?.role === "founder";
  const [featureFlags, setFeatureFlags] = useState(null);
  const [savingFeatureFlags, setSavingFeatureFlags] = useState(false);
  // PM Ownership Model (2026-09) — whatsapp/photo_url live once on the User profile (self-
  // service only) and follow onto every project this person is Project Manager on.
  const canBePm = ["founder", "partner", "pm", "coordinator"].includes(user?.role);
  const [whatsapp, setWhatsapp] = useState("");
  const [photoUrl, setPhotoUrl] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);
  // Founder-only "Settings -> Email health" panel (2026-09-29, HIGH priority bugfix): the
  // founder has zero production visibility into why an email never arrived — this diagnoses
  // EMAIL_SENDS_DISABLED/provider config/sender identity + last 20 send attempts, with a real
  // "Send test email" button that respects the same kill-switch every other send does.
  const [emailHealth, setEmailHealth] = useState(null);
  const [loadingEmailHealth, setLoadingEmailHealth] = useState(false);
  const [sendingEmailTest, setSendingEmailTest] = useState(false);
  const [emailTestResult, setEmailTestResult] = useState(null);

  useEffect(() => { api.get("/v1/settings/me").then((r) => setMe(r.data)); }, []);
  useEffect(() => {
    if (me?.user) { setWhatsapp(me.user.whatsapp || ""); setPhotoUrl(me.user.photo_url || ""); }
  }, [me]);
  useEffect(() => {
    if (!canSeeMargin) return;
    api.get("/v1/settings/exchange-rate").then((r) => setRate(String(r.data.usd_inr_rate)));
    api.get("/v1/settings/commercial-sheet").then((r) => { setSheetUrl(r.data.sheet_url); setSheetInput(r.data.sheet_url || ""); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSeeMargin]);
  useEffect(() => {
    if (!canSeeBank) return;
    api.get("/v1/settings/bank-details").then((r) => setBank(r.data));
    api.get("/v1/settings/build-drive-folders").then((r) => setDriveFolders(r.data));
    api.get("/v1/settings/onboarding-links").then((r) => setOnboardingLinks(r.data));
    api.get("/v1/settings/pricing-editors").then((r) => setPricingEditors(r.data.pricing_editors || []));
    api.get("/v1/settings/agency-quote-recipients").then((r) => setAgencyQuoteRecipients(r.data.agency_quote_recipients || []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSeeBank]);
  useEffect(() => {
    if (!isFounder) return;
    api.get("/v1/settings/feature-flags").then((r) => setFeatureFlags(r.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFounder]);

  const loadEmailHealth = () => {
    setLoadingEmailHealth(true);
    api.get("/v1/settings/email-health").then((r) => setEmailHealth(r.data)).catch(() => {}).finally(() => setLoadingEmailHealth(false));
  };
  useEffect(() => {
    if (!isFounder) return;
    loadEmailHealth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFounder]);

  const sendEmailHealthTest = async () => {
    setSendingEmailTest(true);
    setEmailTestResult(null);
    try {
      const r = await api.post("/v1/settings/email-health/test");
      setEmailTestResult(r.data);
      loadEmailHealth();
    } catch (e) {
      setEmailTestResult({ result: { failed: true, reason: e?.response?.data?.detail?.code || "request_failed" } });
    } finally {
      setSendingEmailTest(false);
    }
  };

  const saveFeatureFlags = async (next) => {
    setSavingFeatureFlags(true);
    try {
      const r = await api.post("/v1/settings/feature-flags", next);
      const { ok, ...saved } = r.data;
      setFeatureFlags(saved);
      toast.success("Feature flags saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.code || "Couldn't save feature flags");
    } finally {
      setSavingFeatureFlags(false);
    }
  };

  const saveOnboardingLinks = async () => {
    setSavingOnboardingLinks(true);
    try {
      const payload = { ...onboardingLinks };
      if (payload.onboarding_ack_response_days !== null && payload.onboarding_ack_response_days !== "") {
        payload.onboarding_ack_response_days = Number(payload.onboarding_ack_response_days);
      }
      const r = await api.post("/v1/settings/onboarding-links", payload);
      const { ok, ...saved } = r.data;
      setOnboardingLinks(saved);
      toast.success("Onboarding links saved — emails will use these live");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.code || "Couldn't save onboarding links");
    } finally {
      setSavingOnboardingLinks(false);
    }
  };

  const savePricingEditors = async () => {
    setSavingPricingEditors(true);
    try {
      const r = await api.post("/v1/settings/pricing-editors", { pricing_editors: pricingEditors });
      setPricingEditors(r.data.pricing_editors);
      toast.success("Pricing editors saved");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === "non_staff_email") {
        toast.error(`Not a founder/partner/finance/pm account: ${detail.emails.join(", ")}`);
      } else {
        toast.error(detail?.code || "Couldn't save pricing editors");
      }
    } finally {
      setSavingPricingEditors(false);
    }
  };

  const saveAgencyQuoteRecipients = async () => {
    setSavingAgencyQuoteRecipients(true);
    try {
      const r = await api.post("/v1/settings/agency-quote-recipients", { agency_quote_recipients: agencyQuoteRecipients });
      setAgencyQuoteRecipients(r.data.agency_quote_recipients);
      toast.success("Agency quote request recipients saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.code || "Couldn't save recipients");
    } finally {
      setSavingAgencyQuoteRecipients(false);
    }
  };

  const saveDriveFolders = async () => {
    setSavingDriveFolders(true);
    try {
      const r = await api.post("/v1/settings/build-drive-folders", driveFolders);
      setDriveFolders({
        freelancer_uploads_folder_id: r.data.freelancer_uploads_folder_id,
        freelancer_rate_card_folder_id: r.data.freelancer_rate_card_folder_id,
        partner_uploads_folder_id: r.data.partner_uploads_folder_id,
        projects_drive_root_folder_id: r.data.projects_drive_root_folder_id,
      });
      toast.success("Drive folder targets saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.code || "Couldn't save Drive folders");
    } finally {
      setSavingDriveFolders(false);
    }
  };

  const saveBank = async () => {
    setSavingBank(true);
    try {
      const r = await api.post("/v1/settings/bank-details", bank);
      setBank(r.data);
      toast.success("Bank details saved — used on every new SOW's Cost & Payment clause");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.code || "Couldn't save bank details");
    } finally {
      setSavingBank(false);
    }
  };

  const saveRate = async () => {
    const v = Number(rate);
    if (!v || v <= 0) { toast.error("Enter a valid positive rate"); return; }
    setSavingRate(true);
    try {
      await api.post("/v1/settings/exchange-rate", { usd_inr_rate: v });
      toast.success(`USD → INR exchange rate saved: ${v}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.code || "Couldn't save exchange rate");
    } finally {
      setSavingRate(false);
    }
  };

  // C2 (2026-02): re-pointing the Commercial Engine to a new sheet is just pasting its URL
  // here — the export, Quote Log, "App Quote — Latest" and Check Sheet button all follow.
  const saveSheet = async () => {
    if (!sheetInput.trim()) { toast.error("Paste a Google Sheet URL or ID"); return; }
    setSavingSheet(true);
    try {
      const r = await api.post("/v1/settings/commercial-sheet", { sheet_url_or_id: sheetInput.trim() });
      setSheetUrl(r.data.sheet_url);
      toast.success("Commercial Engine sheet updated — tabs will auto-create on next export");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.code || "Couldn't save sheet");
    } finally {
      setSavingSheet(false);
    }
  };

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      const r = await api.patch("/auth/me", { whatsapp, photo_url: photoUrl });
      setMe((prev) => ({ ...prev, user: r.data.user }));
      toast.success("Contact info saved — used on every project you're PM on");
    } catch {
      toast.error("Couldn't save contact info");
    } finally {
      setSavingProfile(false);
    }
  };

  return (
    <div className="p-8 space-y-6" data-testid="settings-page">
      <h1 className="font-editorial text-4xl tracking-tight">Settings</h1>
      <section className="rounded-lg border border-[#E7E5E4] bg-[#FFFFFF] p-6 space-y-2">
        <div className="text-xs uppercase tracking-wider text-[#A8A29E]">Profile</div>
        <div className="text-lg">{user?.name}</div>
        <div className="text-sm text-[#57534E]">{user?.email}</div>
        <div className="text-[10px] uppercase tracking-wider text-[#B45309]">{user?.role}</div>
      </section>
      {canBePm && (
        <section className="rounded-lg border border-[#E7E5E4] bg-[#FFFFFF] p-6 space-y-3" data-testid="settings-pm-contact-section">
          <div className="text-xs uppercase tracking-wider text-[#A8A29E]">Project Manager Contact Card</div>
          <p className="text-xs text-[#57534E]">Set once here — follows you onto every project you're assigned Project Manager on (Client Portal PM card, Agency Portal "Your NJM contact"). Name/email stay as above.</p>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#A8A29E]">WhatsApp</label>
            <input data-testid="settings-pm-whatsapp-input" value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} placeholder="+91…"
              className="mt-1 w-full max-w-sm rounded-md border border-[#E7E5E4] bg-[#FFFFFF] px-3 py-2 text-sm focus:outline-none focus:border-[#FFC817]/50" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#A8A29E]">Photo URL</label>
            <input data-testid="settings-pm-photo-input" value={photoUrl} onChange={(e) => setPhotoUrl(e.target.value)} placeholder="https://…"
              className="mt-1 w-full max-w-sm rounded-md border border-[#E7E5E4] bg-[#FFFFFF] px-3 py-2 text-sm focus:outline-none focus:border-[#FFC817]/50" />
          </div>
          <button onClick={saveProfile} disabled={savingProfile} data-testid="settings-pm-save-btn"
            className="rounded-md bg-[#1C1C1E] px-4 py-2 text-xs text-white hover:bg-[#B45309] disabled:opacity-50">
            {savingProfile ? "Saving…" : "Save"}
          </button>
        </section>
      )}
      {canSeeMargin && (
        <section className="rounded-lg border border-[#E7E5E4] bg-[#FFFFFF] p-6 space-y-3">
          <div className="text-xs uppercase tracking-wider text-[#A8A29E]">USD → INR Exchange Rate</div>
          <p className="text-xs text-[#57534E]">Used to bridge Margin (INR) on Foreign-currency quotes in the Quote Builder. Client-facing totals always stay in USD — only the internal margin calculation uses this rate.</p>
          <div className="flex items-center gap-2">
            <input
              data-testid="settings-exchange-rate-input" type="number" min="0" step="0.01" value={rate}
              onChange={(e) => setRate(e.target.value)}
              className="w-32 rounded-md border border-[#E7E5E4] bg-[#FFFFFF] px-3 py-2 text-sm text-[#1C1C1E] focus:outline-none focus:border-[#FFC817]/50"
            />
            <button data-testid="settings-exchange-rate-save" onClick={saveRate} disabled={savingRate} className="rounded-md bg-[#FFC817] px-3 py-2 text-xs font-medium text-[#1C1C1E] hover:bg-[#E6B415] disabled:opacity-50">
              {savingRate ? "Saving…" : "Save"}
            </button>
          </div>
        </section>
      )}
      {canSeeMargin && (
        <section className="rounded-lg border border-[#E7E5E4] bg-[#FFFFFF] p-6 space-y-3">
          <div className="text-xs uppercase tracking-wider text-[#A8A29E]">Commercial Engine Sheet</div>
          <p className="text-xs text-[#57534E]">The Google Sheet that "Send to Commercial Engine Sheet" and "Check Sheet" (in the Quote Builder) read/write. Paste a new Sheet's URL here to re-point everything — its own tabs auto-create on first use, and the app never touches any of the sheet's other existing tabs.</p>
          <div className="flex items-center gap-2">
            <input
              data-testid="settings-commercial-sheet-input" type="text" value={sheetInput} placeholder="https://docs.google.com/spreadsheets/d/..."
              onChange={(e) => setSheetInput(e.target.value)}
              className="flex-1 rounded-md border border-[#E7E5E4] bg-[#FFFFFF] px-3 py-2 text-sm text-[#1C1C1E] focus:outline-none focus:border-[#FFC817]/50"
            />
            <button data-testid="settings-commercial-sheet-save" onClick={saveSheet} disabled={savingSheet} className="rounded-md bg-[#FFC817] px-3 py-2 text-xs font-medium text-[#1C1C1E] hover:bg-[#E6B415] disabled:opacity-50">
              {savingSheet ? "Saving…" : "Save"}
            </button>
            {sheetUrl && (
              <a data-testid="settings-commercial-sheet-open" href={sheetUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-md border border-[#E7E5E4] px-3 py-2 text-xs text-[#57534E] hover:bg-[#F5F5F4]">
                <ExternalLink className="h-3 w-3" /> Open
              </a>
            )}
          </div>
          <p className="text-[10px] text-[#A8A29E]" data-testid="settings-commercial-sheet-access-hint">This app writes to the sheet as a Google service account — it doesn't share the sheet with anyone. If "Check Sheet" or "Open" looks blank or asks for access, share this Google Sheet directly (its own Share button) with whoever needs to view it.</p>
        </section>
      )}
      {canSeeBank && bank && (
        <section className="rounded-lg border border-[#E7E5E4] bg-[#FFFFFF] p-6 space-y-3" data-testid="settings-bank-details-section">
          <div className="text-xs uppercase tracking-wider text-[#A8A29E]">Bank Details &amp; Registered Address</div>
          <p className="text-xs text-[#57534E]">Founder/Partner only. Feeds the "Cost &amp; Payment" and SWIFT clauses on every generated SOW, and the registered address printed on every generated PDF — never visible to any other role.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              ["account_holder_name", "Account Holder Name"],
              ["bank_name", "Bank Name"],
              ["account_number", "Account Number"],
              ["ifsc", "IFSC Code (domestic)"],
              ["swift", "SWIFT Code (overseas)"],
              ["branch", "Branch"],
            ].map(([field, label]) => (
              <label key={field} className="flex flex-col gap-1">
                <span className="text-[10px] uppercase tracking-wider text-[#A8A29E]">{label}</span>
                <input
                  data-testid={`settings-bank-${field}-input`} type="text" value={bank[field] || ""}
                  onChange={(e) => setBank({ ...bank, [field]: e.target.value })}
                  className="rounded-md border border-[#E7E5E4] bg-[#FFFFFF] px-3 py-2 text-sm text-[#1C1C1E] focus:outline-none focus:border-[#FFC817]/50"
                />
              </label>
            ))}
            <label className="flex flex-col gap-1 sm:col-span-2">
              <span className="text-[10px] uppercase tracking-wider text-[#A8A29E]">Registered Business Address</span>
              <textarea
                data-testid="settings-bank-registered_address-input" value={bank.registered_address || ""} rows={2}
                onChange={(e) => setBank({ ...bank, registered_address: e.target.value })}
                className="rounded-md border border-[#E7E5E4] bg-[#FFFFFF] px-3 py-2 text-sm text-[#1C1C1E] focus:outline-none focus:border-[#FFC817]/50"
              />
            </label>
          </div>
          <button data-testid="settings-bank-save" onClick={saveBank} disabled={savingBank} className="rounded-md bg-[#FFC817] px-3 py-2 text-xs font-medium text-[#1C1C1E] hover:bg-[#E6B415] disabled:opacity-50">
            {savingBank ? "Saving…" : "Save Bank Details"}
          </button>
        </section>
      )}
      {canSeeBank && driveFolders && (
        <section className="rounded-lg border border-[#E7E5E4] bg-[#FFFFFF] p-6 space-y-3" data-testid="settings-build-drive-folders-section">
          <div className="text-xs uppercase tracking-wider text-[#A8A29E]">NJM Build — Drive Upload Folders</div>
          <p className="text-xs text-[#57534E]">Founder/Partner only. Two SEPARATE targets, by sensitivity: profile photos go into a shared folder; rate cards go into a restricted, NJM-only folder — <strong>set that rate-card folder's own Drive sharing to "Restricted" (never "Anyone with the link")</strong> when you create it. Both stay inactive until Domain-Wide Delegation is set up.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-[10px] uppercase tracking-wider text-[#A8A29E]">Freelancer Uploads Folder (photos)</span>
              <input
                data-testid="settings-drive-freelancer-uploads-input" type="text" value={driveFolders.freelancer_uploads_folder_id || ""}
                placeholder="Drive folder URL or ID"
                onChange={(e) => setDriveFolders({ ...driveFolders, freelancer_uploads_folder_id: e.target.value })}
                className="rounded-md border border-[#E7E5E4] bg-[#FFFFFF] px-3 py-2 text-sm text-[#1C1C1E] focus:outline-none focus:border-[#FFC817]/50"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[10px] uppercase tracking-wider text-[#A8A29E]">Rate Card Folder (restricted, NJM-only)</span>
              <input
                data-testid="settings-drive-rate-card-input" type="text" value={driveFolders.freelancer_rate_card_folder_id || ""}
                placeholder="Drive folder URL or ID"
                onChange={(e) => setDriveFolders({ ...driveFolders, freelancer_rate_card_folder_id: e.target.value })}
                className="rounded-md border border-[#E7E5E4] bg-[#FFFFFF] px-3 py-2 text-sm text-[#1C1C1E] focus:outline-none focus:border-[#FFC817]/50"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[10px] uppercase tracking-wider text-[#A8A29E]">Partner Company Logos Folder</span>
              <input
                data-testid="settings-drive-partner-logos-input" type="text" value={driveFolders.partner_uploads_folder_id || ""}
                placeholder="Drive folder URL or ID"
                onChange={(e) => setDriveFolders({ ...driveFolders, partner_uploads_folder_id: e.target.value })}
                className="rounded-md border border-[#E7E5E4] bg-[#FFFFFF] px-3 py-2 text-sm text-[#1C1C1E] focus:outline-none focus:border-[#FFC817]/50"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[10px] uppercase tracking-wider text-[#A8A29E]">Community Uploads Folder (post attachments)</span>
              <input
                data-testid="settings-drive-community-uploads-input" type="text" value={driveFolders.community_uploads_folder_id || ""}
                placeholder="Drive folder URL or ID"
                onChange={(e) => setDriveFolders({ ...driveFolders, community_uploads_folder_id: e.target.value })}
                className="rounded-md border border-[#E7E5E4] bg-[#FFFFFF] px-3 py-2 text-sm text-[#1C1C1E] focus:outline-none focus:border-[#FFC817]/50"
              />
            </label>
            <label className="flex flex-col gap-1 sm:col-span-2">
              <span className="text-[10px] uppercase tracking-wider text-[#A8A29E]">Projects Drive Root Folder (auto-creates a folder per project)</span>
              <input
                data-testid="settings-drive-projects-root-input" type="text" value={driveFolders.projects_drive_root_folder_id || ""}
                placeholder="Drive folder URL or ID"
                onChange={(e) => setDriveFolders({ ...driveFolders, projects_drive_root_folder_id: e.target.value })}
                className="rounded-md border border-[#E7E5E4] bg-[#FFFFFF] px-3 py-2 text-sm text-[#1C1C1E] focus:outline-none focus:border-[#FFC817]/50"
              />
              <span className="text-[10px] text-[#A8A29E]">One-time setup. The app auto-creates a dedicated Drive folder for each project under this root (BTS/Receipts/Invoices each get their own subfolder inside it) the first time it's needed — no more manually linking every project's Drive folder by hand.</span>
            </label>
            <label className="flex flex-col gap-1 sm:col-span-2">
              <span className="text-[10px] uppercase tracking-wider text-[#A8A29E]">Partner Agencies Root Folder (one folder per agency, never shared externally)</span>
              <input
                data-testid="settings-drive-partner-agencies-root-input" type="text" value={driveFolders.partner_agencies_drive_root_folder_id || ""}
                placeholder="Drive folder URL or ID"
                onChange={(e) => setDriveFolders({ ...driveFolders, partner_agencies_drive_root_folder_id: e.target.value })}
                className="rounded-md border border-[#E7E5E4] bg-[#FFFFFF] px-3 py-2 text-sm text-[#1C1C1E] focus:outline-none focus:border-[#FFC817]/50"
              />
              <span className="text-[10px] text-[#A8A29E]">Used by "Create Drive workspace" on an agency's page — one NJM-internal folder per agency, never shared with the agency itself.</span>
            </label>
          </div>
          <button data-testid="settings-drive-folders-save" onClick={saveDriveFolders} disabled={savingDriveFolders} className="rounded-md bg-[#FFC817] px-3 py-2 text-xs font-medium text-[#1C1C1E] hover:bg-[#E6B415] disabled:opacity-50">
            {savingDriveFolders ? "Saving…" : "Save Drive Folders"}
          </button>
        </section>
      )}
      {canSeeBank && onboardingLinks && (
        <section className="rounded-lg border border-[#E7E5E4] bg-[#FFFFFF] p-6 space-y-3" data-testid="settings-onboarding-links-section">
          <div className="text-xs uppercase tracking-wider text-[#A8A29E]">NJM Build — Onboarding Links</div>
          <p className="text-xs text-[#57534E]">Founder/Partner only. The links the onboarding emails (Calendly invite, Welcome, Application Received) actually send — live, no placeholders. One WhatsApp group per discipline: Cinematographer→Cinematographers, Photographer→Photographers, Editor/Colorist/VFX Artist→Editors, every other role→Creators.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              ["onboarding_calendly_link", "Calendly Booking Link"],
              ["onboarding_nda_link", "NDA Document Link"],
              ["onboarding_engagement_letter_link", "Engagement Letter Link"],
              ["onboarding_ack_response_days", "Application Ack — Response Days (default 5)"],
            ].map(([field, label]) => (
              <label key={field} className="flex flex-col gap-1">
                <span className="text-[10px] uppercase tracking-wider text-[#A8A29E]">{label}</span>
                <input
                  data-testid={`settings-onboarding-${field}-input`}
                  type={field === "onboarding_ack_response_days" ? "number" : "text"}
                  min={field === "onboarding_ack_response_days" ? 1 : undefined}
                  value={onboardingLinks[field] ?? ""}
                  placeholder={field === "onboarding_ack_response_days" ? "5" : "https://..."}
                  onChange={(e) => setOnboardingLinks({ ...onboardingLinks, [field]: e.target.value })}
                  className="rounded-md border border-[#E7E5E4] bg-[#FFFFFF] px-3 py-2 text-sm text-[#1C1C1E] focus:outline-none focus:border-[#FFC817]/50"
                />
              </label>
            ))}
          </div>
          <div className="text-[10px] uppercase tracking-wider text-[#A8A29E] pt-1">WhatsApp Groups</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              ["whatsapp_group_cinematographers", "Cinematographers Group Link"],
              ["whatsapp_group_photographers", "Photographers Group Link"],
              ["whatsapp_group_editors", "Editors Group Link"],
              ["whatsapp_group_creators", "Creators Group Link (default for every other role)"],
            ].map(([field, label]) => (
              <label key={field} className="flex flex-col gap-1">
                <span className="text-[10px] uppercase tracking-wider text-[#A8A29E]">{label}</span>
                <input
                  data-testid={`settings-onboarding-${field}-input`} type="text" value={onboardingLinks[field] || ""}
                  placeholder="https://chat.whatsapp.com/..."
                  onChange={(e) => setOnboardingLinks({ ...onboardingLinks, [field]: e.target.value })}
                  className="rounded-md border border-[#E7E5E4] bg-[#FFFFFF] px-3 py-2 text-sm text-[#1C1C1E] focus:outline-none focus:border-[#FFC817]/50"
                />
              </label>
            ))}
          </div>
          <button data-testid="settings-onboarding-links-save" onClick={saveOnboardingLinks} disabled={savingOnboardingLinks} className="rounded-md bg-[#FFC817] px-3 py-2 text-xs font-medium text-[#1C1C1E] hover:bg-[#E6B415] disabled:opacity-50">
            {savingOnboardingLinks ? "Saving…" : "Save Onboarding Links"}
          </button>
        </section>
      )}
      {canSeeBank && pricingEditors && (
        <section className="rounded-lg border border-[#E7E5E4] bg-[#FFFFFF] p-6 space-y-3" data-testid="settings-pricing-editors-section">
          <div className="text-xs uppercase tracking-wider text-[#A8A29E]">Pricing Manager — Editors</div>
          <p className="text-xs text-[#57534E]">Founder/Partner only. Founder and Partner can always edit rates; anyone listed here ALSO gets Pricing Manager write access, on top of whatever role they already have.</p>
          <div className="flex flex-wrap gap-1.5">
            {pricingEditors.map((email) => (
              <span key={email} data-testid={`settings-pricing-editor-chip-${email}`} className="inline-flex items-center gap-1.5 rounded-full border border-[#E7E5E4] bg-[#F5F5F4] px-2.5 py-1 text-xs text-[#1C1C1E]">
                {email}
                <button data-testid={`settings-pricing-editor-remove-${email}`} onClick={() => setPricingEditors(pricingEditors.filter((e) => e !== email))} className="text-[#A8A29E] hover:text-red-600">×</button>
              </span>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <input data-testid="settings-pricing-editor-input" type="email" value={newPricingEditor} placeholder="name@njmpro.com"
              onChange={(e) => setNewPricingEditor(e.target.value)}
              className="w-64 rounded-md border border-[#E7E5E4] bg-[#FFFFFF] px-3 py-2 text-sm text-[#1C1C1E] focus:outline-none focus:border-[#FFC817]/50" />
            <button data-testid="settings-pricing-editor-add" onClick={() => {
              const v = newPricingEditor.trim().toLowerCase();
              if (v && !pricingEditors.includes(v)) setPricingEditors([...pricingEditors, v]);
              setNewPricingEditor("");
            }} className="rounded-md border border-[#E7E5E4] px-3 py-2 text-xs text-[#57534E] hover:bg-[#F5F5F4]">Add</button>
          </div>
          <button data-testid="settings-pricing-editors-save" onClick={savePricingEditors} disabled={savingPricingEditors} className="rounded-md bg-[#FFC817] px-3 py-2 text-xs font-medium text-[#1C1C1E] hover:bg-[#E6B415] disabled:opacity-50">
            {savingPricingEditors ? "Saving…" : "Save Pricing Editors"}
          </button>
        </section>
      )}
      {canSeeBank && agencyQuoteRecipients && (
        <section className="rounded-lg border border-[#E7E5E4] bg-[#FFFFFF] p-6 space-y-3" data-testid="settings-agency-quote-recipients-section">
          <div className="text-xs uppercase tracking-wider text-[#A8A29E]">Agency Quote Request Recipients</div>
          <p className="text-xs text-[#57534E]">Founder/Partner only. Everyone listed here gets an email the moment an agency submits a Production Cost Estimate / Quote Request via the Agency Portal.</p>
          <div className="flex flex-wrap gap-1.5">
            {agencyQuoteRecipients.map((email) => (
              <span key={email} data-testid={`settings-agency-quote-recipient-chip-${email}`} className="inline-flex items-center gap-1.5 rounded-full border border-[#E7E5E4] bg-[#F5F5F4] px-2.5 py-1 text-xs text-[#1C1C1E]">
                {email}
                <button data-testid={`settings-agency-quote-recipient-remove-${email}`} onClick={() => setAgencyQuoteRecipients(agencyQuoteRecipients.filter((e) => e !== email))} className="text-[#A8A29E] hover:text-red-600">×</button>
              </span>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <input data-testid="settings-agency-quote-recipient-input" type="email" value={newAgencyQuoteRecipient} placeholder="name@njmpro.com"
              onChange={(e) => setNewAgencyQuoteRecipient(e.target.value)}
              className="w-64 rounded-md border border-[#E7E5E4] bg-[#FFFFFF] px-3 py-2 text-sm text-[#1C1C1E] focus:outline-none focus:border-[#FFC817]/50" />
            <button data-testid="settings-agency-quote-recipient-add" onClick={() => {
              const v = newAgencyQuoteRecipient.trim().toLowerCase();
              if (v && !agencyQuoteRecipients.includes(v)) setAgencyQuoteRecipients([...agencyQuoteRecipients, v]);
              setNewAgencyQuoteRecipient("");
            }} className="rounded-md border border-[#E7E5E4] px-3 py-2 text-xs text-[#57534E] hover:bg-[#F5F5F4]">Add</button>
          </div>
          <button data-testid="settings-agency-quote-recipients-save" onClick={saveAgencyQuoteRecipients} disabled={savingAgencyQuoteRecipients} className="rounded-md bg-[#FFC817] px-3 py-2 text-xs font-medium text-[#1C1C1E] hover:bg-[#E6B415] disabled:opacity-50">
            {savingAgencyQuoteRecipients ? "Saving…" : "Save Recipients"}
          </button>
        </section>
      )}
      {isFounder && featureFlags && (
        <section className="rounded-lg border border-[#E7E5E4] bg-[#FFFFFF] p-6 space-y-3" data-testid="settings-feature-flags-section">
          <div className="text-xs uppercase tracking-wider text-[#A8A29E]">NJM Build Opportunities — Feature Flags</div>
          <p className="text-xs text-[#57534E]">Founder-only. Turns the whole Opportunities surface on/off (staff admin CRUD + Interests, AND the freelancer-facing Opportunities tab + Floor module) — everything 404s cleanly while off, on both sides.</p>
          <label className="flex items-center justify-between gap-3 rounded-md border border-[#E7E5E4] px-3 py-2.5">
            <span className="text-sm text-[#1C1C1E]">Opportunities feature</span>
            <input type="checkbox" data-testid="settings-flag-build-opportunities"
              checked={!!featureFlags.feature_build_opportunities}
              onChange={(e) => setFeatureFlags({ ...featureFlags, feature_build_opportunities: e.target.checked })}
              className="h-4 w-4 accent-[#FFC817]" />
          </label>
          <label className="flex items-center justify-between gap-3 rounded-md border border-[#E7E5E4] px-3 py-2.5">
            <span className="text-sm text-[#1C1C1E]">Notion shadow ingestion (daily cron + "Run now")</span>
            <input type="checkbox" data-testid="settings-flag-opportunity-ingestion"
              checked={!!featureFlags.feature_opportunity_ingestion}
              onChange={(e) => setFeatureFlags({ ...featureFlags, feature_opportunity_ingestion: e.target.checked })}
              className="h-4 w-4 accent-[#FFC817]" />
          </label>
          {featureFlags.feature_opportunity_ingestion && !featureFlags.feature_build_opportunities && (
            <p className="text-[11px] text-red-600" data-testid="settings-flag-ingestion-warning">Ingestion also requires the Opportunities feature above to be ON — it stays 404'd until you turn both on and save.</p>
          )}
          <p className="text-[11px] text-[#A8A29E]">Ingestion never auto-publishes — it only creates/updates rows as "candidate". A row still needs a staff review + explicit Publish in the Opportunities tab before any freelancer can see it.</p>
          <button data-testid="settings-feature-flags-save" onClick={() => saveFeatureFlags(featureFlags)} disabled={savingFeatureFlags}
            className="rounded-md bg-[#FFC817] px-3 py-2 text-xs font-medium text-[#1C1C1E] hover:bg-[#E6B415] disabled:opacity-50">
            {savingFeatureFlags ? "Saving…" : "Save Feature Flags"}
          </button>
        </section>
      )}
      {isFounder && (
        <section className="rounded-lg border border-[#E7E5E4] bg-[#FFFFFF] p-6 space-y-3" data-testid="settings-email-health-section">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase tracking-wider text-[#A8A29E]">Email Health</div>
            <button data-testid="settings-email-health-refresh" onClick={loadEmailHealth} disabled={loadingEmailHealth}
              className="rounded-md border border-[#E7E5E4] px-3 py-1.5 text-xs text-[#57534E] hover:bg-[#F5F5F4] disabled:opacity-50">
              {loadingEmailHealth ? "Refreshing…" : "Refresh"}
            </button>
          </div>
          <p className="text-xs text-[#57534E]">Founder-only. Diagnoses why a production email did or didn't arrive — sends kill-switch, provider config, sender identity, and the last 20 send attempts app-wide.</p>
          {!emailHealth ? (
            <p className="text-xs text-[#A8A29E]">Loading…</p>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="rounded-md border border-[#E7E5E4] px-3 py-2.5 flex items-center justify-between" data-testid="settings-email-health-sends-disabled">
                  <span className="text-xs text-[#1C1C1E]">EMAIL_SENDS_DISABLED <span className="text-[#A8A29E]">({emailHealth.email_sends_disabled_source || "unknown"})</span></span>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider ${emailHealth.email_sends_disabled ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700"}`}>
                    {emailHealth.email_sends_disabled ? "true (blocking all sends)" : "false (sends allowed)"}
                  </span>
                </div>
                <div className="rounded-md border border-[#E7E5E4] px-3 py-2.5 flex items-center justify-between" data-testid="settings-email-health-app-env">
                  <span className="text-xs text-[#1C1C1E]">APP_ENV <span className="text-[#A8A29E]">({emailHealth.app_env_source || "unknown"})</span></span>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider ${emailHealth.app_env === "production" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                    {emailHealth.app_env || "—"}
                  </span>
                </div>
                <div className="rounded-md border border-[#E7E5E4] px-3 py-2.5 flex items-center justify-between" data-testid="settings-email-health-provider-configured">
                  <span className="text-xs text-[#1C1C1E]">Provider configured</span>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider ${emailHealth.provider_configured ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
                    {emailHealth.provider_configured ? "yes" : "no"}
                  </span>
                </div>
                <div className="rounded-md border border-[#E7E5E4] px-3 py-2.5 flex items-center justify-between" data-testid="settings-email-health-allowlist">
                  <span className="text-xs text-[#1C1C1E]">EMAIL_ALLOWLIST <span className="text-[#A8A29E]">({emailHealth.email_allowlist_source || "unknown"})</span></span>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider ${!emailHealth.email_allowlist_enabled ? "bg-[#F5F5F4] text-[#57534E]" : emailHealth.email_allowlist_fail_closed ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"}`}>
                    {!emailHealth.email_allowlist_enabled ? "off" : emailHealth.email_allowlist_fail_closed ? "fail-closed (blocking all)" : `on — ${emailHealth.email_allowlist_entry_count} entries`}
                  </span>
                </div>
                <div className="rounded-md border border-[#E7E5E4] px-3 py-2.5" data-testid="settings-email-health-environment-host">
                  <span className="text-[10px] uppercase tracking-wider text-[#A8A29E] block">Environment host</span>
                  <span className="text-xs text-[#1C1C1E] break-all">{emailHealth.environment_host || "—"}</span>
                </div>
                <div className="rounded-md border border-[#E7E5E4] px-3 py-2.5" data-testid="settings-email-health-sender">
                  <span className="text-[10px] uppercase tracking-wider text-[#A8A29E] block">Sender address</span>
                  <span className="text-xs text-[#1C1C1E]">{emailHealth.sender || "—"}</span>
                </div>
                <div className="rounded-md border border-[#E7E5E4] px-3 py-2.5" data-testid="settings-email-health-reply-to">
                  <span className="text-[10px] uppercase tracking-wider text-[#A8A29E] block">Reply-to</span>
                  <span className="text-xs text-[#1C1C1E]">{emailHealth.reply_to || "—"}</span>
                </div>
              </div>

              {emailHealth.email_allowlist_enabled && (
                <div className="rounded-md border border-[#E7E5E4] px-3 py-2.5" data-testid="settings-email-health-allowlist-entries">
                  <span className="text-[10px] uppercase tracking-wider text-[#A8A29E] block mb-1">
                    {emailHealth.email_allowlist_fail_closed ? `Allowlist parse error — ${emailHealth.email_allowlist_error}` : "Allowlisted recipients (masked)"}
                  </span>
                  {!emailHealth.email_allowlist_fail_closed && (
                    <span className="text-xs text-[#1C1C1E] font-mono break-all">{(emailHealth.email_allowlist_entries || []).join(", ") || "—"}</span>
                  )}
                </div>
              )}

              <div className="flex items-center gap-2 pt-1">
                <button data-testid="settings-email-health-test-send-btn" onClick={sendEmailHealthTest} disabled={sendingEmailTest}
                  className="rounded-md bg-[#1C1C1E] px-4 py-2 text-xs text-white hover:bg-[#B45309] disabled:opacity-50">
                  {sendingEmailTest ? "Sending…" : "Send test email"}
                </button>
                <span className="text-[11px] text-[#A8A29E]">Sends to your own login email ({user?.email}) — respects the kill-switch above.</span>
              </div>
              {emailTestResult && (
                <div data-testid="settings-email-health-test-result" className="rounded-md border border-[#E7E5E4] bg-[#F5F5F4] px-3 py-2.5 text-xs text-[#1C1C1E]">
                  {emailTestResult.result?.blocked ? (
                    <span>Blocked — {emailTestResult.result.reason}</span>
                  ) : emailTestResult.result?.failed ? (
                    <span className="text-red-600">Provider error — {emailTestResult.result.reason}</span>
                  ) : emailTestResult.result?.provider_email_id ? (
                    <span className="text-emerald-700">Sent — provider id {emailTestResult.result.provider_email_id}</span>
                  ) : (
                    <span>{emailTestResult.result?.reason || "Unknown result"}</span>
                  )}
                </div>
              )}

              <div className="text-[10px] uppercase tracking-wider text-[#A8A29E] pt-1">Last 20 send attempts</div>
              {(emailHealth.recent_sends || []).length === 0 ? (
                <p className="text-xs text-[#A8A29E]" data-testid="settings-email-health-empty">No send attempts logged yet.</p>
              ) : (
                <div className="space-y-1" data-testid="settings-email-health-recent-sends">
                  {emailHealth.recent_sends.map((r, i) => {
                    // Bugfix (2026-09-29, Arush Mathur incident): blocked rows must read as
                    // clearly NOT-sent, not just a differently-colored version of "sent".
                    // Bugfix (2026-09-30, production email incident): `outcome` is ALWAYS the
                    // literal string "blocked" for every blocked row (see
                    // kernel/email_gateway.py's send()) — it never carries
                    // "blocked_sends_disabled". The real distinguishing signal is `error`
                    // ("blocked_by_email_sends_disabled_flag" vs "blocked_non_production(...)"
                    // — see that file's two block branches). The previous check compared
                    // `r.outcome` against a value that never occurs, so EVERY blocked row fell
                    // through to "non-prod safeguard" even when it was really the kill switch.
                    const blocked = (r.outcome || "").startsWith("blocked");
                    const isKillSwitch = (r.error || "").startsWith("blocked_by_email_sends_disabled_flag");
                    const label = r.outcome === "sent" ? "SENT" : blocked ? `BLOCKED — ${isKillSwitch ? "sends disabled" : "non-production safeguard"}` : r.outcome === "failed" ? "FAILED" : (r.outcome || "unknown").toUpperCase();
                    return (
                      <div key={i} className="flex items-center justify-between gap-2 rounded-md border border-[#E7E5E4] px-3 py-1.5 text-[11px]" data-testid={`settings-email-health-row-${i}`}>
                        <span className="text-[#57534E]">{r.sent_at ? new Date(r.sent_at).toLocaleString() : "—"}</span>
                        <span className="text-[#1C1C1E] font-mono">{r.template || "—"}</span>
                        <span className="text-[#57534E]">{r.recipient_domain || "—"}</span>
                        <span className={`rounded-full px-2 py-0.5 uppercase tracking-wider font-medium ${r.outcome === "sent" ? "bg-emerald-50 text-emerald-700" : r.outcome === "failed" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-800"}`}>{label}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </section>
      )}
      <section className="rounded-lg border border-[#E7E5E4] bg-[#FFFFFF] p-6">
        <div className="text-xs uppercase tracking-wider text-[#A8A29E] mb-2">Your scopes</div>
        <div className="flex flex-wrap gap-1.5">
          {(me?.scopes || []).map((s) => <span key={s} className="rounded-full border border-[#E7E5E4] px-2 py-0.5 text-[10px] font-mono">{s}</span>)}
        </div>
      </section>
      <button data-testid="settings-logout" onClick={logout} className="rounded-md border border-red-500/30 bg-red-500/5 px-3 py-1.5 text-xs text-red-600">Sign out</button>
    </div>
  );
}
